import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, ChevronDown } from "lucide-react";

const LOGO_FULL = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/0oicyq69_FullLogo_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de agendar uma consulta jurídica.";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial scroll position
    return () => window.removeEventListener("scroll", handleScroll);
  }, [setScrolled]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location, setMobileMenuOpen]);

  const areasDeAtuacao = [
    { label: "Direito do Trabalho", path: "/direito-trabalhista" },
    { label: "Direito Tributário", path: "/direito-tributario" },
    { label: "Direito Imobiliário", path: "/direito-imobiliario" },
    { label: "Direito Previdenciário", path: "/direito-previdenciario" },
    { label: "Direito Cível", path: "/direito-civel" },
    { label: "Direito de Família", path: "/direito-familia" },
    { label: "Direito Penal", path: "/direito-penal" },
  ];

  const isAreaActive = areasDeAtuacao.some(area => location.pathname === area.path);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/98 backdrop-blur-md shadow-sm" : "bg-white"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo">
            <img
              src={LOGO_FULL}
              alt="Helio Lopes Leal Advocacia"
              className="h-12 w-auto"
              style={{ filter: "brightness(0) saturate(100%)" }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`nav-link text-sm tracking-wide ${location.pathname === "/" ? "text-blue-900 font-semibold" : ""}`}
            >
              INÍCIO
            </Link>

            <Link
              to="/sobre"
              className={`nav-link text-sm tracking-wide ${location.pathname === "/sobre" ? "text-blue-900 font-semibold" : ""}`}
            >
              SOBRE
            </Link>

            {/* Dropdown Áreas de Atuação */}
            <div className="relative group">
              <button
                className={`nav-link text-sm tracking-wide flex items-center gap-1 ${isAreaActive ? "text-blue-900 font-semibold" : ""}`}
              >
                ÁREAS DE ATUAÇÃO
                <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-3 min-w-[240px]">
                  {areasDeAtuacao.map((area) => (
                    <Link
                      key={area.path}
                      to={area.path}
                      className={`block px-5 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                        location.pathname === area.path ? "text-blue-900 font-medium bg-blue-50" : "text-gray-700"
                      }`}
                    >
                      {area.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/contato"
              className={`nav-link text-sm tracking-wide ${location.pathname === "/contato" ? "text-blue-900 font-semibold" : ""}`}
            >
              CONTATO
            </Link>

            <Link
              to="/julgados"
              className={`nav-link text-sm tracking-wide ${location.pathname === "/julgados" ? "text-blue-900 font-semibold" : ""}`}
            >
              JULGADOS
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-2.5 rounded text-sm font-medium tracking-wide transition-all flex items-center gap-2"
              data-testid="header-cta-btn"
            >
              <MessageCircle className="w-4 h-4" />
              FALE CONOSCO
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-1">
            <Link
              to="/"
              className="block py-3 text-sm font-medium text-gray-800 hover:text-blue-900 border-b border-gray-100"
            >
              INÍCIO
            </Link>
            <Link
              to="/sobre"
              className="block py-3 text-sm font-medium text-gray-800 hover:text-blue-900 border-b border-gray-100"
            >
              SOBRE
            </Link>
            <div className="py-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 mb-2 tracking-wider">ÁREAS DE ATUAÇÃO</p>
              <div className="space-y-1 pl-3">
                {areasDeAtuacao.map((area) => (
                  <Link
                    key={area.path}
                    to={area.path}
                    className="block py-2 text-sm text-gray-700 hover:text-blue-900"
                  >
                    {area.label}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/contato"
              className="block py-3 text-sm font-medium text-gray-800 hover:text-blue-900"
            >
              CONTATO
            </Link>
            <Link
              to="/julgados"
              className="block py-3 text-sm font-medium text-gray-800 hover:text-blue-900 border-t border-gray-100"
            >
              JULGADOS RELEVANTES
            </Link>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4 bg-blue-900 text-white text-center py-3 rounded text-sm font-medium"
            >
              FALE COM UM ADVOGADO
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
